import { CommonModule } from '@angular/common';
import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { ComponentsModule } from '@freelancer/components';
import {
  AiConsultantDisplayMessageType,
  AiConsultantMessageRole,
} from '@freelancer/datastore/collections';
import { LocalStorage } from '@freelancer/local-storage';
import { TrackingModule } from '@freelancer/tracking';
import { UiModule } from '@freelancer/ui';
import { Assets } from '@freelancer/ui/assets';
import { ButtonColor } from '@freelancer/ui/button';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { SpinnerSize } from '@freelancer/ui/spinner';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import type {
  AiConsultantMessageDraftProject,
  AiConsultantMessageLoading,
  AiConsultantMessageValid,
} from 'app/ai-consultant/ai-consultant.types';

@Component({
  standalone: true,
  imports: [CommonModule, ComponentsModule, TrackingModule, UiModule],
  selector: 'app-ai-consultant-message',
  template: `
    <fl-user-avatar
      [flHideMobile]="true"
      [users]="
        message.role === AiConsultantMessageRole.ASSISTANT
          ? [aiUser]
          : [clientUser]
      "
      [isOnline]="true"
      [size]="AvatarSize.LARGE"
      [disableAvatarBorder]="true"
      [disableOnlineIndicatorBorder]="true"
      [flMarginBottom]="'xxxsmall'"
      [flMarginRight]="'small'"
    ></fl-user-avatar>
    <div class="MessageSection">
      <ng-container
        *ngIf="
          message.type === AiConsultantDisplayMessageType.CHAT;
          else draftProject
        "
      >
        <ng-template [ngTemplateOutlet]="mobileUserSection"> </ng-template>
        <fl-interactive-text
          class="MessageContent"
          [content]="message.content"
          [fontColor]="FontColor.LIGHT"
          [fontSize]="TextSize.SMALL"
          [link]="true"
          [flMarginBottom]="'xsmall'"
        ></fl-interactive-text>
      </ng-container>
      <ng-template #draftProject>
        <ng-container
          *ngIf="
            message.type === AiConsultantDisplayMessageType.DRAFT_PROJECT;
            else loadingState
          "
        >
          <ng-template [ngTemplateOutlet]="mobileUserSection"> </ng-template>
          <fl-interactive-text
            class="MessageContent"
            [content]="message.content"
            [fontColor]="FontColor.LIGHT"
            [fontSize]="TextSize.SMALL"
            [link]="true"
            [flMarginBottom]="'xsmall'"
          ></fl-interactive-text>
          <div class="Button-container">
            <div
              class="Button-reviewBorder"
              [flMarginBottom]="'xsmall'"
              [flMarginBottomTablet]="'none'"
              [flMarginRight]="'xsmall'"
            >
              <fl-button
                i18n="Review the draft project button"
                class="Button"
                flTrackingSection="AiConsultantWidget"
                flTrackingLabel="AiConsultantWidgetReviewProject"
                [link]="'/post-project'"
                [queryParams]="{ fromAiConsultant: 't' }"
                [color]="ButtonColor.TEXT_LIGHT"
              >
                Review Project
              </fl-button>
            </div>
            <div class="Button-saveBorder">
              <fl-button
                i18n="Save conversation button"
                class="Button"
                flTrackingSection="AiConsultantWidget"
                flTrackingLabel="AiConsultantWidgetSaveConversation"
                [color]="ButtonColor.TEXT_LIGHT"
                (click)="handleSaveConversation()"
              >
                Save Conversation
              </fl-button>
            </div>
          </div>
        </ng-container>
      </ng-template>
      <ng-template #loadingState>
        <ng-container
          *ngIf="
            message.type === AiConsultantDisplayMessageType.LOADING;
            else errorState
          "
        >
          <ng-template [ngTemplateOutlet]="mobileUserSection"> </ng-template>
          <div class="MessageLoadingState">
            <fl-spinner
              [flTrackingLabel]="'AiConsultantMessageLoading'"
              [flMarginRight]="'xxsmall'"
              [size]="SpinnerSize.SMALL"
            ></fl-spinner>
            <fl-text
              i18n="AI Consultant loading state text"
              [color]="FontColor.LIGHT"
              [size]="TextSize.SMALL"
            >
              Thinking...
            </fl-text>
          </div>
        </ng-container>
      </ng-template>
      <ng-template #errorState>
        <ng-template [ngTemplateOutlet]="mobileUserSection"> </ng-template>
        <div class="MessageErrorState">
          <fl-icon
            *ngIf="message.type === AiConsultantDisplayMessageType.ERROR"
            [name]="'ui-warning-outline'"
            [color]="IconColor.ERROR"
            [size]="IconSize.SMALL"
            [flMarginRight]="'xxsmall'"
          ></fl-icon>
          <fl-text
            [color]="FontColor.LIGHT"
            [size]="TextSize.SMALL"
          >
            <ng-container
              i18n="Ava chat is not available"
              *ngIf="
                message.type === AiConsultantDisplayMessageType.ERROR;
                else limitMessage
              "
            >
              Ava isn't available right now, try again soon.
            </ng-container>
            <ng-template #limitMessage>
              <ng-container
                i18n="The user has reached their limit on AI consultant"
              >
                I'm sorry, but we've reached the limit on your free
                consultation. If you want to keep going, you can
                <fl-link
                  flTrackingLabel="LimitReachedPostProject"
                  [link]="'/post-project'"
                  [size]="TextSize.SMALL"
                  >post a project now
                </fl-link>
                and start talking to professional freelancers at no cost.
              </ng-container>
            </ng-template>
          </fl-text>
        </div>
      </ng-template>
    </div>

    <ng-template #mobileUserSection>
      <div
        class="MessageSection-mobileUserSection"
        [flMarginBottom]="'xsmall'"
        [flMarginBottomTablet]="'none'"
      >
        <fl-user-avatar
          [flShowMobile]="true"
          [disableAvatarBorder]="true"
          [disableOnlineIndicatorBorder]="true"
          [users]="
            message.role === AiConsultantMessageRole.ASSISTANT
              ? [aiUser]
              : [clientUser]
          "
          [isOnline]="true"
          [size]="AvatarSize.MID"
          [flMarginRight]="'small'"
        ></fl-user-avatar>
        <div
          class="Username"
          flTrackingSection="AiConsultantWidget"
          [flMarginBottomTablet]="'xsmall'"
        >
          <fl-text
            i18n="AI Consultant chat user role"
            [color]="FontColor.LIGHT"
            [weight]="FontWeight.BOLD"
            [size]="TextSize.SMALL"
            [flMarginRight]="'xxsmall'"
          >
            {{
              message.role === AiConsultantMessageRole.ASSISTANT
                ? 'Ava (AI Agent)'
                : 'You'
            }}
          </fl-text>

          <div
            *ngIf="
              message.role === 'assistant' &&
              (message.type === AiConsultantDisplayMessageType.CHAT ||
                message.type === AiConsultantDisplayMessageType.DRAFT_PROJECT)
            "
            aria-label="Audio Player"
            i18n-aria-label="i18n Audio Player"
          >
            <fl-picture
              *ngIf="messageAudioState === 'stopped'"
              class="AudioPlayer AudioPlayer-button"
              flTrackingLabel="PlayButton"
              alt="Play button"
              i18n-alt="i18n Play button"
              [width]="41"
              [height]="36"
              [src]="'ai-consultant/play.png'"
              (click)="handlePlayAudio()"
            >
            </fl-picture>

            <fl-picture
              *ngIf="messageAudioState === 'playing'"
              class="AudioPlayer AudioPlayer-button"
              flTrackingLabel="PauseButton"
              alt="Pause button"
              i18n-alt="Pause button"
              [width]="41"
              [height]="36"
              [src]="'ai-consultant/pause.png'"
              (click)="handlePauseAudio()"
            >
            </fl-picture>

            <fl-spinner
              *ngIf="messageAudioState === 'loading'"
              aria-label="Loading State"
              i18n-aria-label="i18n Loading State"
              class="AudioPlayer"
              flTrackingLabel="AudioLoading"
              [size]="SpinnerSize.SMALL"
            ></fl-spinner>
          </div>
        </div>
      </div>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./ai-consultant-message.component.scss'],
})
export class AiConsultantMessageComponent implements OnChanges {
  ButtonColor = ButtonColor;
  AvatarSize = AvatarSize;
  FontColor = FontColor;
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  SpinnerSize = SpinnerSize;
  TextSize = TextSize;
  AiConsultantDisplayMessageType = AiConsultantDisplayMessageType;
  AiConsultantMessageRole = AiConsultantMessageRole;

  aiUser = {
    avatar: this.assets.getUrl('ai-consultant/ai-agent-avatar.jpg'),
    username: 'ava',
  };

  clientUser = {
    avatar: this.assets.getUrl('ai-consultant/ai-user-avatar-v2.jpg'),
    username: 'you',
  };

  @Input() message:
    | AiConsultantMessageValid
    | AiConsultantMessageLoading
    | AiConsultantMessageDraftProject;
  @Input() messageAudioState: 'playing' | 'stopped' | 'loading' = 'stopped';

  @Output() playAudio = new EventEmitter<string>();
  @Output() pauseAudio = new EventEmitter<void>();
  @Output() saveConversation = new EventEmitter<void>();

  constructor(private assets: Assets, private localStorage: LocalStorage) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('message' in changes) {
      if (this.message.type === AiConsultantDisplayMessageType.DRAFT_PROJECT) {
        this.localStorage.set('aiConsultantGeneratedProjectDraft', {
          title: this.message.title,
          description: this.message.description,
        });
      }
    }
  }

  handlePlayAudio(): void {
    if (this.message.type !== AiConsultantDisplayMessageType.LOADING) {
      this.playAudio.emit(this.message.content);
    }
  }

  handlePauseAudio(): void {
    this.pauseAudio.emit();
  }

  handleSaveConversation(): void {
    this.saveConversation.emit();
  }
}
